import { useCallback } from 'react'

import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { logger } from 'utils/logging'

const PERMISSION_ASKED_SESSION_KEY = 'chatWidget.notificationPermissionAsked'

const isSupported = () => typeof window !== 'undefined' && 'Notification' in window

const isConstructorUnsupportedError = (error: unknown) => error instanceof TypeError

const canConstructNotification = () => {
  try {
    new Notification('').close?.()
    return true
  } catch (error) {
    return !isConstructorUnsupportedError(error)
  }
}

// Fallback for when sessionStorage is unavailable (private mode, blocked site data).
let askedThisPageLoad = false
let notificationFailureReported = false

export type NotificationPermissionRequestSource = 'launcher' | 'send'

const hasAskedThisSession = () => {
  if (askedThisPageLoad) return true
  try {
    return window.sessionStorage.getItem(PERMISSION_ASKED_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

const markAskedThisSession = () => {
  askedThisPageLoad = true
  try {
    window.sessionStorage.setItem(PERMISSION_ASKED_SESSION_KEY, '1')
  } catch {
    // The page-load flag above still prevents a second prompt.
  }
}

/** Test-only: forget that permission was asked, so each test starts from a fresh session. */
export const resetNotificationPermissionRequestForTests = () => {
  askedThisPageLoad = false
  notificationFailureReported = false
  try {
    window.sessionStorage.removeItem(PERMISSION_ASKED_SESSION_KEY)
  } catch {
    // ignore
  }
}

/**
 * Asks for desktop-notification permission. MUST be called synchronously from a user gesture (a click or
 * a keypress handler): Chrome and Safari ignore `Notification.requestPermission()` outside one, which is
 * why asking on mount never showed a prompt (QA-398).
 *
 * Asks at most once per browser session, and only while the permission is still "default" — a "denied"
 * or "granted" answer is never re-prompted. Where it does not prompt (permission already decided, no
 * Notification API as on Safari iOS, or a constructor that throws as on Chrome Android) it reports that
 * state once per session instead.
 */
export const requestNotificationPermission = (source: NotificationPermissionRequestSource) => {
  if (hasAskedThisSession()) return
  markAskedThisSession()

  if (!isSupported() || Notification.permission !== 'default' || !canConstructNotification()) {
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_NOTIFICATION_PERMISSION_STATE, {
      permission: isSupported() && Notification.permission !== 'default' ? Notification.permission : 'unsupported',
      source,
    })
    return
  }

  let isAnswerLogged = false
  const logAnswer = (permission: NotificationPermission) => {
    if (isAnswerLogged) return
    isAnswerLogged = true
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_NOTIFICATION_PERMISSION_ANSWERED, { permission, source })
  }

  try {
    // Older Safari only supports the callback form and returns undefined instead of a promise.
    const result = Notification.requestPermission(logAnswer) as Promise<NotificationPermission> | undefined
    if (result && typeof result.then === 'function') {
      // Tells whether the prompt now shows and what clients answer: without it a "no notifications" report
      // cannot be told apart from a denied permission.
      result.then(logAnswer).catch((error: unknown) => {
        logAnswer(Notification.permission)
        logger.warn('ChatWidget: notification permission request failed', error, {
          context: { component: 'useBrowserNotifications', action: 'requestPermission' },
        })
      })
    }
  } catch (error) {
    logger.warn('ChatWidget: notification permission request threw', error, {
      context: { component: 'useBrowserNotifications', action: 'requestPermission' },
    })
  }
}

/** A system notification raised by the chat widget. */
export interface ChatBrowserNotification {
  title: string
  body: string
  /** Collapses repeat notifications for the same conversation into one OS popup. */
  tag?: string
}

/**
 * Returns a `notify` helper that raises an OS notification. Deciding WHEN to notify (thread on screen or
 * not) is the caller's job — see `useUnreadNotifications`. Permission is requested separately, on a user
 * gesture, via `requestNotificationPermission`.
 *
 * `notify` is a no-op when notifications are unsupported or not granted; the unread badge + tab-title
 * badge remain the guaranteed fallback in those cases.
 */
export const useBrowserNotifications = () =>
  useCallback(({ title, body, tag }: ChatBrowserNotification) => {
    if (!isSupported() || Notification.permission !== 'granted') return

    try {
      new Notification(title, { body, tag })
    } catch (error) {
      if (isConstructorUnsupportedError(error) || notificationFailureReported) return
      notificationFailureReported = true
      logger.warn('ChatWidget: desktop notification could not be shown', error, {
        context: { component: 'useBrowserNotifications', action: 'notify' },
      })
    }
  }, [])
